import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Loader2, GitMerge, AlertCircle, Eye, X, RefreshCw, CheckCircle, Users } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ConfidenceBadge, MatchDetails } from '@/components/deduplication';
import { findCandidateDuplicates, MatchResult } from '@/lib/deduplication-utils';
import { Link } from 'react-router-dom';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Candidate {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email_primary: string | null;
  phone_primary: string | null;
  city: string | null;
}

interface DuplicateGroup {
  primary: Candidate;
  duplicates: Array<{ candidate: Candidate; match: MatchResult }>;
}

interface GroupSelection {
  primaryId: string;
  selectedIds: Set<string>;
}

interface CandidateDeduplicationPanelProps {
  onMergeComplete?: () => void;
}

export const CandidateDeduplicationPanel = ({ onMergeComplete }: CandidateDeduplicationPanelProps) => {
  const [duplicates, setDuplicates] = useState<DuplicateGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<number | null>(null);
  const [selectedCandidate, setSelectedCandidate] = useState<Candidate | null>(null);
  const [groupSelections, setGroupSelections] = useState<Record<number, GroupSelection>>({});
  const [ignoredPairs, setIgnoredPairs] = useState<Set<string>>(new Set());
  const { toast } = useToast();

  useEffect(() => {
    fetchIgnoredPairs().then(() => fetchAndDetectDuplicates());
  }, []);

  const fetchIgnoredPairs = async () => {
    try {
      const { data } = await supabase
        .from('duplicate_suggestions')
        .select('primary_entity_id, duplicate_entity_id')
        .eq('entity_type', 'candidate')
        .eq('status', 'ignored');

      if (data) {
        const pairs = new Set<string>();
        data.forEach(row => {
          pairs.add(`${row.primary_entity_id}-${row.duplicate_entity_id}`);
          pairs.add(`${row.duplicate_entity_id}-${row.primary_entity_id}`);
        });
        setIgnoredPairs(pairs);
      }
    } catch (error) {
      console.error('Error fetching ignored pairs:', error);
    }
  };

  const fetchAndDetectDuplicates = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('candidates')
        .select('id, first_name, last_name, email_primary, phone_primary, city')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const allGroups = findCandidateDuplicates(data || [], 80) as DuplicateGroup[];
      
      // Filter out ignored pairs
      const filteredGroups = allGroups.map(group => {
        const filteredDuplicates = group.duplicates.filter(d => {
          const pairKey = `${group.primary.id}-${d.candidate.id}`;
          return !ignoredPairs.has(pairKey);
        });
        return { ...group, duplicates: filteredDuplicates };
      }).filter(group => group.duplicates.length > 0);

      setDuplicates(filteredGroups);
      
      // Initialize selections: all selected by default, first one as primary
      const initialSelections: Record<number, GroupSelection> = {};
      filteredGroups.forEach((group, idx) => {
        const allIds = [group.primary.id, ...group.duplicates.map(d => d.candidate.id)];
        initialSelections[idx] = {
          primaryId: group.primary.id,
          selectedIds: new Set(allIds),
        };
      });
      setGroupSelections(initialSelections);
    } catch (error) {
      console.error('Error fetching candidates:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch candidates',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleCandidateSelection = (groupIndex: number, candidateId: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedIds);
      
      if (newSelected.has(candidateId)) {
        // Don't allow deselecting the primary
        if (candidateId === current.primaryId) {
          toast({ title: 'Cannot deselect primary candidate', variant: 'destructive' });
          return prev;
        }
        newSelected.delete(candidateId);
      } else {
        newSelected.add(candidateId);
      }
      
      return {
        ...prev,
        [groupIndex]: { ...current, selectedIds: newSelected }
      };
    });
  };

  const setPrimaryCandidate = (groupIndex: number, candidateId: string) => {
    setGroupSelections(prev => {
      const current = prev[groupIndex];
      const newSelected = new Set(current.selectedIds);
      // Ensure the new primary is selected
      newSelected.add(candidateId);
      
      return {
        ...prev,
        [groupIndex]: { primaryId: candidateId, selectedIds: newSelected }
      };
    });
  };

  const handleMergeSelected = async (groupIndex: number) => {
    const selection = groupSelections[groupIndex];
    const group = duplicates[groupIndex];
    
    const selectedCount = selection.selectedIds.size;
    if (selectedCount < 2) {
      toast({ title: 'Select at least 2 candidates to merge', variant: 'destructive' });
      return;
    }

    const duplicatesToMerge = [...selection.selectedIds].filter(id => id !== selection.primaryId);

    if (!confirm(`Merge ${duplicatesToMerge.length} candidate(s) into the primary? This action cannot be undone.`)) {
      return;
    }

    setProcessing(groupIndex);
    try {
      for (const duplicateId of duplicatesToMerge) {
        // Update all related tables to point to the primary candidate
        const updatePromises = [
          supabase.from('candidate_applications').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_experience').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_education').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_skills').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_projects').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_resumes').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('communications').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_ai_insights').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
          supabase.from('candidate_job_scores').update({ candidate_id: selection.primaryId }).eq('candidate_id', duplicateId),
        ];

        await Promise.all(updatePromises);

        // Delete records that can't be transferred (or delete duplicates)
        await Promise.all([
          supabase.from('candidate_awards').delete().eq('candidate_id', duplicateId),
          supabase.from('candidate_share_links').delete().eq('candidate_id', duplicateId),
          supabase.from('candidate_aliases').delete().eq('candidate_id', duplicateId),
        ]);

        // Log the merge event
        const { error: eventError } = await supabase.from('candidate_dedupe_events').insert([{
          primary_candidate_id: selection.primaryId,
          merged_candidate_id: duplicateId,
          notes: 'Merged via Data Quality dashboard'
        }]);
        
        if (eventError) {
          console.error('Error logging merge event:', eventError);
        }

        // Finally delete the duplicate candidate
        const { error: deleteError } = await supabase.from('candidates').delete().eq('id', duplicateId);
        
        if (deleteError) {
          console.error('Error deleting candidate:', deleteError);
          throw new Error(`Failed to delete candidate: ${deleteError.message}`);
        }
      }

      toast({ title: `Merged ${duplicatesToMerge.length} candidate(s) successfully` });
      fetchAndDetectDuplicates();
      onMergeComplete?.();
    } catch (error: any) {
      console.error('Error merging candidates:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to merge candidates',
        variant: 'destructive',
      });
    } finally {
      setProcessing(null);
    }
  };

  const dismissGroup = async (groupIndex: number) => {
    const group = duplicates[groupIndex];
    
    // Persist ignored pairs to database
    const ignoreRecords = group.duplicates.map(d => ({
      entity_type: 'candidate',
      primary_entity_id: group.primary.id,
      duplicate_entity_id: d.candidate.id,
      status: 'ignored',
      match_score: d.match.score,
      reason: 'Dismissed by user',
    }));

    try {
      await supabase.from('duplicate_suggestions').insert(ignoreRecords);
      
      // Update local ignored pairs
      const newIgnored = new Set(ignoredPairs);
      group.duplicates.forEach(d => {
        newIgnored.add(`${group.primary.id}-${d.candidate.id}`);
        newIgnored.add(`${d.candidate.id}-${group.primary.id}`);
      });
      setIgnoredPairs(newIgnored);
    } catch (error) {
      console.error('Error saving ignored suggestion:', error);
    }

    setDuplicates(prev => prev.filter((_, i) => i !== groupIndex));
    setGroupSelections(prev => {
      const updated = { ...prev };
      delete updated[groupIndex];
      return updated;
    });
    toast({ title: 'Duplicate group ignored and will not appear again' });
  };

  const getCandidateName = (candidate: Candidate) => {
    const name = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim();
    return name || 'Unnamed Candidate';
  };

  const getAllCandidatesInGroup = (group: DuplicateGroup) => {
    return [
      { candidate: group.primary, match: null as MatchResult | null, isPrimary: true },
      ...group.duplicates.map(d => ({ candidate: d.candidate, match: d.match, isPrimary: false }))
    ];
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-muted-foreground">Analyzing candidate records...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-blue-500" />
            Candidate Deduplication
          </h2>
          <p className="text-sm text-muted-foreground">
            Select candidates to merge and choose which one to keep as primary
          </p>
        </div>
        <Button onClick={fetchAndDetectDuplicates} variant="outline" size="sm" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Rescan
        </Button>
      </div>

      {duplicates.length === 0 ? (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex items-center gap-4 p-6">
            <div className="rounded-full bg-emerald-500/20 p-3">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold">No Duplicates Found</h3>
              <p className="text-sm text-muted-foreground">Your candidate data looks clean!</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <>
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Found {duplicates.length} potential duplicate group{duplicates.length !== 1 ? 's' : ''}.
              Use checkboxes to select which candidates to merge and radio buttons to choose the primary.
            </AlertDescription>
          </Alert>

          <div className="space-y-4">
            {duplicates.map((group, groupIndex) => {
              const selection = groupSelections[groupIndex];
              if (!selection) return null;
              
              const allCandidates = getAllCandidatesInGroup(group);
              const selectedCount = selection.selectedIds.size;
              const isProcessing = processing === groupIndex;

              return (
                <Card key={group.primary.id} className="overflow-hidden">
                  <CardHeader className="bg-muted/50 py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="rounded-full bg-blue-500/20 p-2">
                          <Users className="h-4 w-4 text-blue-500" />
                        </div>
                        <div>
                          <CardTitle className="text-base">
                            Duplicate Group {groupIndex + 1}
                          </CardTitle>
                          <CardDescription>
                            {selectedCount} of {allCandidates.length} selected for merge
                          </CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <ConfidenceBadge
                          confidence={group.duplicates[0]?.match.confidence || 'none'}
                          score={group.duplicates[0]?.match.score || 0}
                        />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => dismissGroup(groupIndex)}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Dismiss this group</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 space-y-3">
                    <RadioGroup
                      value={selection.primaryId}
                      onValueChange={(value) => setPrimaryCandidate(groupIndex, value)}
                      className="space-y-2"
                    >
                      {allCandidates.map(({ candidate, match }) => {
                        const isSelected = selection.selectedIds.has(candidate.id);
                        const isPrimary = selection.primaryId === candidate.id;

                        return (
                          <div
                            key={candidate.id}
                            className={`border rounded-lg p-4 transition-all ${
                              isPrimary 
                                ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                                : isSelected 
                                  ? 'border-border bg-muted/30' 
                                  : 'border-border/50 bg-muted/10 opacity-60'
                            }`}
                          >
                            <div className="flex items-start gap-4">
                              {/* Checkbox for inclusion */}
                              <div className="flex items-center gap-3 pt-1">
                                <Checkbox
                                  id={`check-${candidate.id}`}
                                  checked={isSelected}
                                  onCheckedChange={() => toggleCandidateSelection(groupIndex, candidate.id)}
                                  disabled={isPrimary}
                                />
                                <RadioGroupItem
                                  value={candidate.id}
                                  id={`radio-${candidate.id}`}
                                  disabled={!isSelected}
                                />
                              </div>

                              {/* Candidate Info */}
                              <div className="flex-1 space-y-1">
                                <Label
                                  htmlFor={`radio-${candidate.id}`}
                                  className="flex items-center gap-2 cursor-pointer font-semibold"
                                >
                                  {getCandidateName(candidate)}
                                  {isPrimary && (
                                    <Badge variant="default" className="text-xs">Primary</Badge>
                                  )}
                                </Label>
                                <div className="text-sm text-muted-foreground space-y-0.5">
                                  {candidate.email_primary && <p>{candidate.email_primary}</p>}
                                  {candidate.phone_primary && <p>{candidate.phone_primary}</p>}
                                  {candidate.city && <p>{candidate.city}</p>}
                                </div>
                                {match && <MatchDetails details={match.details} />}
                              </div>

                              {/* Actions */}
                              <div className="flex items-center gap-2">
                                {match && (
                                  <ConfidenceBadge confidence={match.confidence} score={match.score} />
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedCandidate(candidate)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </RadioGroup>

                    {/* Merge Button */}
                    <Button
                      className="w-full gap-2"
                      onClick={() => handleMergeSelected(groupIndex)}
                      disabled={isProcessing || selectedCount < 2}
                    >
                      {isProcessing ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <GitMerge className="h-4 w-4" />
                      )}
                      Merge {selectedCount - 1} Selected into Primary
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </>
      )}

      {/* Candidate Preview Dialog */}
      <Dialog open={!!selectedCandidate} onOpenChange={() => setSelectedCandidate(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Candidate Details</DialogTitle>
            <DialogDescription>
              Preview candidate information
            </DialogDescription>
          </DialogHeader>
          {selectedCandidate && (
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-lg">{getCandidateName(selectedCandidate)}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Email</p>
                  <p>{selectedCandidate.email_primary || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Phone</p>
                  <p>{selectedCandidate.phone_primary || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">City</p>
                  <p>{selectedCandidate.city || 'Not provided'}</p>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to={`/recruiter/candidates?id=${selectedCandidate.id}`}>
                  <Button variant="outline">View Full Profile</Button>
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
